import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatINR, imgUrl } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Fuel, Gauge, Settings2, User, ArrowLeft, Loader2, Phone, BadgeCheck, Calendar, Palette, Car,
} from "lucide-react";
import { toast } from "sonner";

export default function CarDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [active, setActive] = useState(0);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get(`/vehicles/${id}`).then((r) => {
      setVehicle(r.data);
      setForm((f) => ({ ...f, message: `Hi, I'm interested in your ${r.data.title}. Is it still available?` }));
    }).catch(() => navigate("/motors"));
  }, [id, navigate]);

  if (!vehicle) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-navy" /></div>;

  const submitEnquiry = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/enquiries", { property_id: vehicle.id, ...form });
      toast.success("Enquiry sent! The seller will reach out soon.");
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch { toast.error("Could not send enquiry."); }
    finally { setSending(false); }
  };

  const specs = [
    { icon: Calendar, label: "Year", value: vehicle.year },
    { icon: Gauge, label: "KM Driven", value: `${vehicle.km_driven.toLocaleString("en-IN")} km` },
    { icon: Fuel, label: "Fuel", value: vehicle.fuel_type },
    { icon: Settings2, label: "Transmission", value: vehicle.transmission },
    { icon: User, label: "Owners", value: `${vehicle.owners}` },
    { icon: Car, label: "Body", value: vehicle.body_type },
    { icon: Palette, label: "Color", value: vehicle.color || "—" },
  ];

  return (
    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 py-8">
      <button data-testid="car-back-btn" onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-navy mb-4"><ArrowLeft className="h-4 w-4" /> Back</button>

      <div className="grid grid-cols-4 gap-3 h-[300px] sm:h-[440px]">
        <div className="col-span-4 sm:col-span-3 rounded-2xl overflow-hidden">
          <img src={imgUrl(vehicle.images?.[active])} alt={vehicle.title} className="w-full h-full object-cover" data-testid="car-gallery-main" />
        </div>
        <div className="hidden sm:flex col-span-1 flex-col gap-3">
          {vehicle.images?.slice(0, 3).map((img, i) => (
            <button key={i} onClick={() => setActive(i)} className={`flex-1 rounded-xl overflow-hidden border-2 transition-all ${active === i ? "border-navy" : "border-transparent"}`}>
              <img src={imgUrl(img)} alt="" className="w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8 mt-8">
        <div className="lg:col-span-2">
          <div className="flex items-center gap-2">
            <Badge className="bg-navy hover:bg-navy">{vehicle.fuel_type}</Badge>
            <Badge variant="outline" className="border-slate-300 text-slate-600">{vehicle.body_type}</Badge>
            <Badge variant="outline" className="border-slate-300 text-slate-600">{vehicle.brand}</Badge>
          </div>
          <h1 className="mt-3 font-display text-3xl lg:text-4xl font-semibold tracking-tight text-slate-900">{vehicle.title}</h1>
          <p className="mt-2 text-slate-500">{vehicle.city}{vehicle.state ? `, ${vehicle.state}` : ""}</p>
          <p className="mt-5 font-display text-4xl font-bold text-navy">{formatINR(vehicle.price)}</p>

          <div className="mt-6 grid grid-cols-2 sm:grid-cols-4 gap-4">
            {specs.map((s) => (
              <div key={s.label} className="bg-slate-50 border border-slate-200 rounded-xl p-4">
                <s.icon className="h-5 w-5 text-navy" />
                <p className="mt-2 text-xs uppercase tracking-wide text-slate-500">{s.label}</p>
                <p className="font-display font-semibold text-slate-900">{s.value}</p>
              </div>
            ))}
          </div>

          <div className="mt-8">
            <h2 className="font-display text-xl font-semibold text-slate-900">Description</h2>
            <p className="mt-3 text-slate-600 leading-relaxed">{vehicle.description}</p>
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="lg:sticky lg:top-40 bg-white border border-slate-200 rounded-2xl p-6">
            <p className="text-xs uppercase tracking-[0.2em] font-semibold text-gold-dark">Contact Seller</p>
            <div className="mt-3 flex items-center gap-3">
              <div className="h-11 w-11 rounded-full bg-navy flex items-center justify-center text-white"><User className="h-5 w-5" /></div>
              <div>
                <p className="font-display font-semibold text-slate-900 flex items-center gap-1.5">{vehicle.contact_name}{vehicle.owner_verified && <BadgeCheck className="h-4 w-4 text-navy" />}</p>
                <p className="text-sm text-slate-500 flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> {vehicle.contact_phone}</p>
              </div>
            </div>
            <form onSubmit={submitEnquiry} className="mt-6 space-y-3">
              <div><Label className="text-xs">Your Name</Label><Input data-testid="car-enquiry-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
              <div><Label className="text-xs">Email</Label><Input data-testid="car-enquiry-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
              <div><Label className="text-xs">Phone</Label><Input data-testid="car-enquiry-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
              <div><Label className="text-xs">Message</Label><Textarea data-testid="car-enquiry-message" rows={3} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} /></div>
              <Button data-testid="car-enquiry-submit" type="submit" disabled={sending} className="w-full bg-gold hover:bg-gold-dark text-navy font-semibold">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send Enquiry"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
}
